import { useState, useEffect } from 'react';
import { useAuth } from '../lib/auth';
import { useLanguage } from '../lib/language';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Switch } from '../components/ui/switch';
import { Progress } from '../components/ui/progress';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import Sidebar from '../components/Sidebar';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import {
  Loader2,
  Plus,
  Trash2,
  Heart,
  Users,
  Calendar,
  DollarSign,
  Clock,
  MapPin,
  Copy,
  Check,
  X,
  ChevronDown,
  ChevronUp,
  ExternalLink,
  Mail,
  Phone,
  UtensilsCrossed,
  UserPlus,
  ListChecks,
  Wallet,
  Eye,
  EyeOff,
  Lock,
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const RSVP_CONFIG = {
  pending: { label: 'Oczekuje', className: 'bg-yellow-500/20 text-yellow-600 border-yellow-500/30' },
  confirmed: { label: 'Potwierdzona', className: 'bg-green-500/20 text-green-600 border-green-500/30' },
  declined: { label: 'Odmowa', className: 'bg-red-500/20 text-red-600 border-red-500/30' },
};

const GUEST_GROUPS = ['Rodzina Panny Mlodej', 'Rodzina Pana Mlodego', 'Przyjaciele', 'Praca', 'Inni'];

const BUDGET_CATEGORIES = [
  'Fotografia', 'Wideo', 'Lokalizacja', 'Catering', 'Dekoracje', 'Muzyka / DJ',
  'Kwiaty', 'Suknia / Garnitur', 'Transport', 'Zaproszenia', 'Tort', 'Inne',
];

const emptyGuest = {
  name: '', email: '', phone: '', group: '', rsvp: 'pending', dietary: '', plus_one: false, notes: '',
};

const emptyAgendaItem = {
  time: '', event: '', location: '', notes: '',
};

const emptyBudgetItem = {
  category: '', description: '', amount: '', paid: false,
};

export default function WeddingPlannerPage() {
  const { user } = useAuth();
  const { t, language } = useLanguage();

  const [loading, setLoading] = useState(true);
  const [weddings, setWeddings] = useState([]);
  const [selectedWedding, setSelectedWedding] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [creating, setCreating] = useState(false);
  const [copiedLink, setCopiedLink] = useState(false);

  // Create form
  const [createForm, setCreateForm] = useState({
    couple_name: '', wedding_date: '', budget_total: '', public: false, password: '',
  });

  // Guest form
  const [showGuestForm, setShowGuestForm] = useState(false);
  const [guestForm, setGuestForm] = useState({ ...emptyGuest });
  const [addingGuest, setAddingGuest] = useState(false);

  // Agenda form
  const [showAgendaForm, setShowAgendaForm] = useState(false);
  const [agendaForm, setAgendaForm] = useState({ ...emptyAgendaItem });
  const [addingAgenda, setAddingAgenda] = useState(false);

  // Budget form
  const [showBudgetForm, setShowBudgetForm] = useState(false);
  const [budgetForm, setBudgetForm] = useState({ ...emptyBudgetItem });

  // Delete confirmation
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  useEffect(() => {
    fetchWeddings();
  }, []);

  const fetchWeddings = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/weddings`);
      setWeddings(response.data.weddings || []);
    } catch (error) {
      toast.error('Nie udalo sie zaladowac wesel');
    } finally {
      setLoading(false);
    }
  };

  const fetchWeddingDetail = async (id) => {
    setDetailLoading(true);
    try {
      const response = await axios.get(`${API}/weddings/${id}`);
      setSelectedWedding(response.data);
    } catch (error) {
      toast.error('Nie udalo sie zaladowac szczegolow');
    } finally {
      setDetailLoading(false);
    }
  };

  const handleCreateWedding = async () => {
    if (!createForm.couple_name.trim()) {
      toast.error('Nazwa pary jest wymagana');
      return;
    }
    if (!createForm.wedding_date) {
      toast.error('Data slubu jest wymagana');
      return;
    }
    setCreating(true);
    try {
      const payload = {
        ...createForm,
        budget_total: createForm.budget_total ? parseFloat(createForm.budget_total) : 0,
      };
      await axios.post(`${API}/weddings`, payload);
      toast.success('Plan wesela utworzony');
      setShowCreateModal(false);
      setCreateForm({ couple_name: '', wedding_date: '', budget_total: '', public: false, password: '' });
      fetchWeddings();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Blad tworzenia planu');
    } finally {
      setCreating(false);
    }
  };

  const handleUpdateWedding = async (id, data) => {
    try {
      const response = await axios.put(`${API}/weddings/${id}`, data);
      setSelectedWedding(response.data);
      toast.success('Zaktualizowano');
      fetchWeddings();
    } catch (error) {
      toast.error('Blad aktualizacji');
    }
  };

  const handleDeleteWedding = async (id) => {
    try {
      await axios.delete(`${API}/weddings/${id}`);
      toast.success('Plan wesela usuniety');
      setDeleteConfirm(null);
      setSelectedWedding(null);
      fetchWeddings();
    } catch (error) {
      toast.error('Blad usuwania');
    }
  };

  const handleAddGuest = async () => {
    if (!guestForm.name.trim()) {
      toast.error('Imie goscia jest wymagane');
      return;
    }
    setAddingGuest(true);
    try {
      await axios.post(`${API}/weddings/${selectedWedding.id}/guests`, guestForm);
      toast.success('Gosc dodany');
      setGuestForm({ ...emptyGuest });
      setShowGuestForm(false);
      fetchWeddingDetail(selectedWedding.id);
    } catch (error) {
      toast.error('Blad dodawania goscia');
    } finally {
      setAddingGuest(false);
    }
  };

  const handleAddAgendaItem = async () => {
    if (!agendaForm.time || !agendaForm.event) {
      toast.error('Godzina i wydarzenie sa wymagane');
      return;
    }
    setAddingAgenda(true);
    try {
      await axios.post(`${API}/weddings/${selectedWedding.id}/agenda`, agendaForm);
      toast.success('Punkt programu dodany');
      setAgendaForm({ ...emptyAgendaItem });
      setShowAgendaForm(false);
      fetchWeddingDetail(selectedWedding.id);
    } catch (error) {
      toast.error('Blad dodawania punktu');
    } finally {
      setAddingAgenda(false);
    }
  };

  const handleAddBudgetItem = () => {
    if (!budgetForm.category || !budgetForm.amount) {
      toast.error('Kategoria i kwota sa wymagane');
      return;
    }
    const newItem = {
      ...budgetForm,
      amount: parseFloat(budgetForm.amount),
    };
    const updatedItems = [...(selectedWedding.budget_items || []), newItem];
    handleUpdateWedding(selectedWedding.id, { budget_items: updatedItems });
    setBudgetForm({ ...emptyBudgetItem });
    setShowBudgetForm(false);
  };

  const handleToggleBudgetPaid = (index) => {
    const items = [...(selectedWedding.budget_items || [])];
    items[index] = { ...items[index], paid: !items[index].paid };
    handleUpdateWedding(selectedWedding.id, { budget_items: items });
  };

  const handleRemoveBudgetItem = (index) => {
    const items = (selectedWedding.budget_items || []).filter((_, i) => i !== index);
    handleUpdateWedding(selectedWedding.id, { budget_items: items });
  };

  const handleCopyPublicLink = () => {
    const link = `${window.location.origin}/public/wedding/${selectedWedding.id}`;
    navigator.clipboard.writeText(link);
    setCopiedLink(true);
    toast.success('Link skopiowany do schowka');
    setTimeout(() => setCopiedLink(false), 2000);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('pl-PL', { day: 'numeric', month: 'long', year: 'numeric' });
  };

  const daysUntilWedding = (dateStr) => {
    if (!dateStr) return null;
    const diff = Math.ceil((new Date(dateStr) - new Date()) / (1000 * 60 * 60 * 24));
    return diff;
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('pl-PL', { style: 'currency', currency: 'PLN' }).format(amount || 0);
  };

  // Budget calculations
  const budgetItems = selectedWedding?.budget_items || [];
  const budgetTotal = selectedWedding?.budget_total || 0;
  const budgetSpent = budgetItems.reduce((sum, item) => sum + (item.amount || 0), 0);
  const budgetPaid = budgetItems.filter(i => i.paid).reduce((sum, item) => sum + (item.amount || 0), 0);
  const budgetProgress = budgetTotal > 0 ? Math.min((budgetSpent / budgetTotal) * 100, 100) : 0;

  // Guest stats
  const guests = selectedWedding?.guests || [];
  const guestConfirmed = guests.filter(g => g.rsvp === 'confirmed').length;
  const guestDeclined = guests.filter(g => g.rsvp === 'declined').length;
  const guestPending = guests.filter(g => g.rsvp === 'pending').length;

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Sidebar activePath="/weddings" />

      <main className="lg:ml-64 p-6 lg:p-10">
        {/* Header */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-8">
          <div>
            <h1 className="text-3xl font-bold">Planer wesel</h1>
            <p className="text-muted-foreground mt-1">
              {weddings.length} {weddings.length === 1 ? 'wesele' : 'wesel'} w bazie
            </p>
          </div>
          <Button onClick={() => setShowCreateModal(true)}>
            <Plus className="w-4 h-4 mr-2" />
            Nowy plan
          </Button>
        </div>

        {/* Wedding Cards List */}
        {!selectedWedding && (
          <>
            {weddings.length === 0 ? (
              <Card className="bg-card border-border">
                <CardContent className="p-12 text-center">
                  <Heart className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
                  <h3 className="text-lg font-medium mb-2">Brak planow wesel</h3>
                  <p className="text-sm text-muted-foreground mb-4">
                    Dodaj pierwszy plan wesela, aby rozpoczac
                  </p>
                  <Button onClick={() => setShowCreateModal(true)}>
                    <Plus className="w-4 h-4 mr-2" />
                    Nowy plan
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
                {weddings.map((wedding, idx) => {
                  const days = daysUntilWedding(wedding.wedding_date);
                  return (
                    <motion.div
                      key={wedding.id}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.3, delay: idx * 0.05 }}
                    >
                      <Card
                        className="bg-card border-border hover:border-primary/30 transition-colors cursor-pointer group"
                        onClick={() => fetchWeddingDetail(wedding.id)}
                      >
                        <CardHeader className="pb-3">
                          <div className="flex items-start justify-between">
                            <div className="flex items-center gap-3">
                              <div className="w-10 h-10 rounded-full bg-pink-500/10 flex items-center justify-center">
                                <Heart className="w-5 h-5 text-pink-500" />
                              </div>
                              <div>
                                <CardTitle className="text-lg group-hover:text-primary transition-colors">
                                  {wedding.couple_name}
                                </CardTitle>
                                <p className="text-sm text-muted-foreground">
                                  {formatDate(wedding.wedding_date)}
                                </p>
                              </div>
                            </div>
                            {wedding.public && (
                              <Badge variant="secondary" className="text-xs">
                                <Eye className="w-3 h-3 mr-1" />
                                Publiczne
                              </Badge>
                            )}
                          </div>
                        </CardHeader>
                        <CardContent className="pt-0">
                          <div className="grid grid-cols-3 gap-3">
                            <div className="text-center p-2 bg-secondary/50 rounded-sm">
                              <Users className="w-4 h-4 mx-auto mb-1 text-muted-foreground" />
                              <p className="text-sm font-semibold">{wedding.guest_count ?? 0}</p>
                              <p className="text-xs text-muted-foreground">Gosci</p>
                            </div>
                            <div className="text-center p-2 bg-secondary/50 rounded-sm">
                              <DollarSign className="w-4 h-4 mx-auto mb-1 text-muted-foreground" />
                              <p className="text-sm font-semibold">
                                {wedding.budget_total ? `${Math.round(wedding.budget_total / 1000)}k` : '-'}
                              </p>
                              <p className="text-xs text-muted-foreground">Budzet</p>
                            </div>
                            <div className="text-center p-2 bg-secondary/50 rounded-sm">
                              <Calendar className="w-4 h-4 mx-auto mb-1 text-muted-foreground" />
                              <p className="text-sm font-semibold">
                                {days !== null ? (days > 0 ? days : days === 0 ? 'Dzis' : Math.abs(days)) : '-'}
                              </p>
                              <p className="text-xs text-muted-foreground">
                                {days !== null ? (days > 0 ? 'Dni' : days === 0 ? '' : 'Dni temu') : ''}
                              </p>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    </motion.div>
                  );
                })}
              </div>
            )}
          </>
        )}

        {/* Wedding Detail View */}
        {selectedWedding && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
          >
            {detailLoading ? (
              <div className="flex items-center justify-center py-20">
                <Loader2 className="w-8 h-8 animate-spin" />
              </div>
            ) : (
              <>
                {/* Detail Header */}
                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
                  <div className="flex items-center gap-4">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setSelectedWedding(null)}
                    >
                      <X className="w-4 h-4 mr-1" />
                      Zamknij
                    </Button>
                    <div>
                      <h2 className="text-2xl font-bold flex items-center gap-2">
                        <Heart className="w-5 h-5 text-pink-500" />
                        {selectedWedding.couple_name}
                      </h2>
                      <p className="text-muted-foreground">
                        {formatDate(selectedWedding.wedding_date)}
                        {daysUntilWedding(selectedWedding.wedding_date) > 0 && (
                          <span className="ml-2 text-primary">
                            (za {daysUntilWedding(selectedWedding.wedding_date)} dni)
                          </span>
                        )}
                      </p>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    {selectedWedding.public && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={handleCopyPublicLink}
                      >
                        {copiedLink ? (
                          <Check className="w-4 h-4 mr-1 text-green-500" />
                        ) : (
                          <Copy className="w-4 h-4 mr-1" />
                        )}
                        {copiedLink ? 'Skopiowano' : 'Kopiuj link publiczny'}
                      </Button>
                    )}
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-destructive hover:text-destructive"
                      onClick={() => setDeleteConfirm(selectedWedding.id)}
                    >
                      <Trash2 className="w-4 h-4 mr-1" />
                      Usun
                    </Button>
                  </div>
                </div>

                {/* Stats Row */}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
                  {[
                    { label: 'Gosci', value: guests.length, icon: Users, color: 'text-blue-500' },
                    { label: 'Potwierdzonych', value: guestConfirmed, icon: Check, color: 'text-green-500' },
                    { label: 'Budzet', value: formatCurrency(budgetTotal), icon: Wallet, color: 'text-purple-500' },
                    { label: 'Wydano', value: formatCurrency(budgetSpent), icon: DollarSign, color: 'text-orange-500' },
                  ].map((stat, i) => (
                    <motion.div
                      key={stat.label}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: i * 0.05 }}
                    >
                      <Card>
                        <CardContent className="p-4 flex items-center gap-3">
                          <div className="w-10 h-10 bg-secondary rounded-sm flex items-center justify-center">
                            <stat.icon className={`w-5 h-5 ${stat.color}`} />
                          </div>
                          <div>
                            <p className="text-lg font-bold">{stat.value}</p>
                            <p className="text-xs text-muted-foreground">{stat.label}</p>
                          </div>
                        </CardContent>
                      </Card>
                    </motion.div>
                  ))}
                </div>

                {/* Detail Tabs */}
                <Tabs defaultValue="details" className="space-y-6">
                  <TabsList className="bg-card border border-border">
                    <TabsTrigger value="details" className="data-[state=active]:bg-secondary">
                      <Heart className="w-4 h-4 mr-2" />
                      Szczegoly
                    </TabsTrigger>
                    <TabsTrigger value="guests" className="data-[state=active]:bg-secondary">
                      <Users className="w-4 h-4 mr-2" />
                      Goscie ({guests.length})
                    </TabsTrigger>
                    <TabsTrigger value="agenda" className="data-[state=active]:bg-secondary">
                      <ListChecks className="w-4 h-4 mr-2" />
                      Agenda
                    </TabsTrigger>
                    <TabsTrigger value="budget" className="data-[state=active]:bg-secondary">
                      <Wallet className="w-4 h-4 mr-2" />
                      Budzet
                    </TabsTrigger>
                  </TabsList>

                  {/* Details Tab */}
                  <TabsContent value="details">
                    <Card className="bg-card border-border">
                      <CardHeader>
                        <CardTitle>Informacje o weselu</CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-6">
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                          <div className="space-y-2">
                            <Label>Nazwa pary</Label>
                            <Input
                              value={selectedWedding.couple_name || ''}
                              onChange={(e) => setSelectedWedding({ ...selectedWedding, couple_name: e.target.value })}
                              onBlur={() => handleUpdateWedding(selectedWedding.id, { couple_name: selectedWedding.couple_name })}
                            />
                          </div>
                          <div className="space-y-2">
                            <Label>Data slubu</Label>
                            <Input
                              type="date"
                              value={selectedWedding.wedding_date || ''}
                              onChange={(e) => setSelectedWedding({ ...selectedWedding, wedding_date: e.target.value })}
                              onBlur={() => handleUpdateWedding(selectedWedding.id, { wedding_date: selectedWedding.wedding_date })}
                            />
                          </div>
                          <div className="space-y-2">
                            <Label>Budzet calkowity (PLN)</Label>
                            <Input
                              type="number"
                              value={selectedWedding.budget_total || ''}
                              onChange={(e) => setSelectedWedding({ ...selectedWedding, budget_total: parseFloat(e.target.value) || 0 })}
                              onBlur={() => handleUpdateWedding(selectedWedding.id, { budget_total: selectedWedding.budget_total })}
                            />
                          </div>
                          <div className="space-y-2">
                            <Label>Haslo dostepu (opcjonalne)</Label>
                            <Input
                              type="password"
                              value={selectedWedding.password || ''}
                              onChange={(e) => setSelectedWedding({ ...selectedWedding, password: e.target.value })}
                              onBlur={() => handleUpdateWedding(selectedWedding.id, { password: selectedWedding.password })}
                              placeholder="Tylko jesli wesele publiczne"
                            />
                          </div>
                        </div>
                        <div className="flex items-center gap-3 pt-2">
                          <Switch
                            checked={selectedWedding.public || false}
                            onCheckedChange={(checked) => {
                              setSelectedWedding({ ...selectedWedding, public: checked });
                              handleUpdateWedding(selectedWedding.id, { public: checked });
                            }}
                          />
                          <div>
                            <p className="text-sm font-medium">Publiczny link</p>
                            <p className="text-xs text-muted-foreground">
                              Goscie moga zobaczyc plan wesela pod publicznym linkiem
                            </p>
                          </div>
                        </div>
                        {selectedWedding.public && (
                          <div className="flex items-center gap-2 p-3 bg-secondary/50 rounded-sm">
                            <ExternalLink className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                            <code className="text-sm text-muted-foreground flex-1 truncate">
                              {window.location.origin}/public/wedding/{selectedWedding.id}
                            </code>
                            <Button variant="outline" size="sm" onClick={handleCopyPublicLink}>
                              {copiedLink ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                            </Button>
                          </div>
                        )}

                        {/* RSVP Summary */}
                        <div className="pt-4 border-t border-border">
                          <h4 className="text-sm font-semibold mb-3">Podsumowanie RSVP</h4>
                          <div className="grid grid-cols-3 gap-4">
                            <div className="text-center p-3 bg-yellow-500/10 rounded-sm">
                              <p className="text-2xl font-bold text-yellow-600">{guestPending}</p>
                              <p className="text-xs text-muted-foreground">Oczekuje</p>
                            </div>
                            <div className="text-center p-3 bg-green-500/10 rounded-sm">
                              <p className="text-2xl font-bold text-green-600">{guestConfirmed}</p>
                              <p className="text-xs text-muted-foreground">Potwierdzone</p>
                            </div>
                            <div className="text-center p-3 bg-red-500/10 rounded-sm">
                              <p className="text-2xl font-bold text-red-600">{guestDeclined}</p>
                              <p className="text-xs text-muted-foreground">Odmowy</p>
                            </div>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </TabsContent>

                  {/* Guests Tab */}
                  <TabsContent value="guests">
                    <Card className="bg-card border-border">
                      <CardHeader className="flex flex-row items-center justify-between">
                        <CardTitle>Lista gosci ({guests.length})</CardTitle>
                        <Button size="sm" onClick={() => setShowGuestForm(!showGuestForm)}>
                          {showGuestForm ? (
                            <>
                              <X className="w-4 h-4 mr-2" />
                              Anuluj
                            </>
                          ) : (
                            <>
                              <UserPlus className="w-4 h-4 mr-2" />
                              Dodaj goscia
                            </>
                          )}
                        </Button>
                      </CardHeader>
                      <CardContent>
                        {/* Add Guest Form */}
                        <AnimatePresence>
                          {showGuestForm && (
                            <motion.div
                              initial={{ opacity: 0, height: 0 }}
                              animate={{ opacity: 1, height: 'auto' }}
                              exit={{ opacity: 0, height: 0 }}
                              className="overflow-hidden"
                            >
                              <div className="p-4 mb-6 bg-secondary/30 rounded-sm border border-border space-y-4">
                                <h4 className="font-semibold">Nowy gosc</h4>
                                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                                  <div className="space-y-1">
                                    <Label>Imie i nazwisko *</Label>
                                    <Input
                                      value={guestForm.name}
                                      onChange={(e) => setGuestForm({ ...guestForm, name: e.target.value })}
                                      placeholder="Jan Kowalski"
                                    />
                                  </div>
                                  <div className="space-y-1">
                                    <Label>Email</Label>
                                    <Input
                                      type="email"
                                      value={guestForm.email}
                                      onChange={(e) => setGuestForm({ ...guestForm, email: e.target.value })}
                                      placeholder="jan@email.com"
                                    />
                                  </div>
                                  <div className="space-y-1">
                                    <Label>Telefon</Label>
                                    <Input
                                      type="tel"
                                      value={guestForm.phone}
                                      onChange={(e) => setGuestForm({ ...guestForm, phone: e.target.value })}
                                      placeholder="+48 123 456 789"
                                    />
                                  </div>
                                  <div className="space-y-1">
                                    <Label>Grupa</Label>
                                    <Select value={guestForm.group} onValueChange={(v) => setGuestForm({ ...guestForm, group: v })}>
                                      <SelectTrigger><SelectValue placeholder="Wybierz grupe" /></SelectTrigger>
                                      <SelectContent>
                                        {GUEST_GROUPS.map(g => (
                                          <SelectItem key={g} value={g}>{g}</SelectItem>
                                        ))}
                                      </SelectContent>
                                    </Select>
                                  </div>
                                  <div className="space-y-1">
                                    <Label>Status RSVP</Label>
                                    <Select value={guestForm.rsvp} onValueChange={(v) => setGuestForm({ ...guestForm, rsvp: v })}>
                                      <SelectTrigger><SelectValue /></SelectTrigger>
                                      <SelectContent>
                                        <SelectItem value="pending">Oczekuje</SelectItem>
                                        <SelectItem value="confirmed">Potwierdzona</SelectItem>
                                        <SelectItem value="declined">Odmowa</SelectItem>
                                      </SelectContent>
                                    </Select>
                                  </div>
                                  <div className="space-y-1">
                                    <Label>Preferencje dietetyczne</Label>
                                    <Input
                                      value={guestForm.dietary}
                                      onChange={(e) => setGuestForm({ ...guestForm, dietary: e.target.value })}
                                      placeholder="Wegetarianin, bezglutenowe..."
                                    />
                                  </div>
                                </div>
                                <div className="flex items-center gap-6">
                                  <div className="flex items-center gap-2">
                                    <Switch
                                      checked={guestForm.plus_one}
                                      onCheckedChange={(checked) => setGuestForm({ ...guestForm, plus_one: checked })}
                                    />
                                    <Label>Osoba towarzyszaca (+1)</Label>
                                  </div>
                                </div>
                                <div className="space-y-1">
                                  <Label>Notatki</Label>
                                  <Textarea
                                    value={guestForm.notes}
                                    onChange={(e) => setGuestForm({ ...guestForm, notes: e.target.value })}
                                    placeholder="Dodatkowe uwagi..."
                                    rows={2}
                                  />
                                </div>
                                <div className="flex justify-end gap-2">
                                  <Button variant="outline" onClick={() => { setShowGuestForm(false); setGuestForm({ ...emptyGuest }); }}>
                                    Anuluj
                                  </Button>
                                  <Button onClick={handleAddGuest} disabled={addingGuest}>
                                    {addingGuest && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                                    Dodaj goscia
                                  </Button>
                                </div>
                              </div>
                            </motion.div>
                          )}
                        </AnimatePresence>

                        {/* Guests Table */}
                        {guests.length > 0 ? (
                          <div className="overflow-x-auto">
                            <Table>
                              <TableHeader>
                                <TableRow>
                                  <TableHead className="pl-4">Gosc</TableHead>
                                  <TableHead>Kontakt</TableHead>
                                  <TableHead>Grupa</TableHead>
                                  <TableHead>RSVP</TableHead>
                                  <TableHead>Dieta</TableHead>
                                  <TableHead className="text-center">+1</TableHead>
                                </TableRow>
                              </TableHeader>
                              <TableBody>
                                {guests.map((guest, idx) => (
                                  <motion.tr
                                    key={guest.id || idx}
                                    initial={{ opacity: 0 }}
                                    animate={{ opacity: 1 }}
                                    transition={{ delay: idx * 0.02 }}
                                    className="border-b transition-colors hover:bg-muted/50"
                                  >
                                    <TableCell className="pl-4 py-3">
                                      <div className="flex items-center gap-2">
                                        <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                                          <span className="text-xs font-semibold text-primary">
                                            {guest.name?.charAt(0)?.toUpperCase() || '?'}
                                          </span>
                                        </div>
                                        <span className="font-medium">{guest.name}</span>
                                      </div>
                                    </TableCell>
                                    <TableCell>
                                      <div className="space-y-0.5">
                                        {guest.email && (
                                          <div className="flex items-center gap-1 text-xs text-muted-foreground">
                                            <Mail className="w-3 h-3" />
                                            {guest.email}
                                          </div>
                                        )}
                                        {guest.phone && (
                                          <div className="flex items-center gap-1 text-xs text-muted-foreground">
                                            <Phone className="w-3 h-3" />
                                            {guest.phone}
                                          </div>
                                        )}
                                      </div>
                                    </TableCell>
                                    <TableCell>
                                      <span className="text-sm text-muted-foreground">{guest.group || '-'}</span>
                                    </TableCell>
                                    <TableCell>
                                      <Badge className={RSVP_CONFIG[guest.rsvp]?.className || ''}>
                                        {RSVP_CONFIG[guest.rsvp]?.label || guest.rsvp}
                                      </Badge>
                                    </TableCell>
                                    <TableCell>
                                      {guest.dietary ? (
                                        <div className="flex items-center gap-1 text-xs text-muted-foreground">
                                          <UtensilsCrossed className="w-3 h-3" />
                                          {guest.dietary}
                                        </div>
                                      ) : (
                                        <span className="text-xs text-muted-foreground">-</span>
                                      )}
                                    </TableCell>
                                    <TableCell className="text-center">
                                      {guest.plus_one ? (
                                        <Check className="w-4 h-4 text-green-500 mx-auto" />
                                      ) : (
                                        <X className="w-4 h-4 text-muted-foreground mx-auto" />
                                      )}
                                    </TableCell>
                                  </motion.tr>
                                ))}
                              </TableBody>
                            </Table>
                          </div>
                        ) : (
                          <div className="text-center py-10">
                            <Users className="w-10 h-10 mx-auto mb-3 text-muted-foreground" />
                            <p className="text-muted-foreground">Brak gosci na liscie</p>
                            <Button size="sm" className="mt-3" onClick={() => setShowGuestForm(true)}>
                              <UserPlus className="w-4 h-4 mr-2" />
                              Dodaj pierwszego goscia
                            </Button>
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  </TabsContent>

                  {/* Agenda Tab */}
                  <TabsContent value="agenda">
                    <Card className="bg-card border-border">
                      <CardHeader className="flex flex-row items-center justify-between">
                        <CardTitle>Program dnia</CardTitle>
                        <Button size="sm" onClick={() => setShowAgendaForm(!showAgendaForm)}>
                          {showAgendaForm ? (
                            <>
                              <X className="w-4 h-4 mr-2" />
                              Anuluj
                            </>
                          ) : (
                            <>
                              <Plus className="w-4 h-4 mr-2" />
                              Dodaj punkt
                            </>
                          )}
                        </Button>
                      </CardHeader>
                      <CardContent>
                        {/* Add Agenda Form */}
                        <AnimatePresence>
                          {showAgendaForm && (
                            <motion.div
                              initial={{ opacity: 0, height: 0 }}
                              animate={{ opacity: 1, height: 'auto' }}
                              exit={{ opacity: 0, height: 0 }}
                              className="overflow-hidden"
                            >
                              <div className="p-4 mb-6 bg-secondary/30 rounded-sm border border-border space-y-4">
                                <h4 className="font-semibold">Nowy punkt programu</h4>
                                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                                  <div className="space-y-1">
                                    <Label>Godzina *</Label>
                                    <Input
                                      type="time"
                                      value={agendaForm.time}
                                      onChange={(e) => setAgendaForm({ ...agendaForm, time: e.target.value })}
                                    />
                                  </div>
                                  <div className="space-y-1">
                                    <Label>Wydarzenie *</Label>
                                    <Input
                                      value={agendaForm.event}
                                      onChange={(e) => setAgendaForm({ ...agendaForm, event: e.target.value })}
                                      placeholder="np. Ceremonia slubna"
                                    />
                                  </div>
                                  <div className="space-y-1">
                                    <Label>Lokalizacja</Label>
                                    <Input
                                      value={agendaForm.location}
                                      onChange={(e) => setAgendaForm({ ...agendaForm, location: e.target.value })}
                                      placeholder="np. Kosciol sw. Anny"
                                    />
                                  </div>
                                  <div className="space-y-1">
                                    <Label>Notatki</Label>
                                    <Input
                                      value={agendaForm.notes}
                                      onChange={(e) => setAgendaForm({ ...agendaForm, notes: e.target.value })}
                                      placeholder="Dodatkowe informacje"
                                    />
                                  </div>
                                </div>
                                <div className="flex justify-end gap-2">
                                  <Button variant="outline" onClick={() => { setShowAgendaForm(false); setAgendaForm({ ...emptyAgendaItem }); }}>
                                    Anuluj
                                  </Button>
                                  <Button onClick={handleAddAgendaItem} disabled={addingAgenda}>
                                    {addingAgenda && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                                    Dodaj punkt
                                  </Button>
                                </div>
                              </div>
                            </motion.div>
                          )}
                        </AnimatePresence>

                        {/* Timeline */}
                        {(selectedWedding.agenda || []).length > 0 ? (
                          <div className="relative">
                            {/* Timeline Line */}
                            <div className="absolute left-[18px] top-3 bottom-3 w-0.5 bg-border" />

                            <div className="space-y-0">
                              {(selectedWedding.agenda || [])
                                .sort((a, b) => (a.time || '').localeCompare(b.time || ''))
                                .map((item, idx) => (
                                  <motion.div
                                    key={idx}
                                    initial={{ opacity: 0, x: -10 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    transition={{ delay: idx * 0.05 }}
                                    className="relative flex gap-4 py-4"
                                  >
                                    {/* Timeline Dot */}
                                    <div className="relative z-10 w-9 h-9 rounded-full bg-primary/10 border-2 border-primary flex items-center justify-center flex-shrink-0">
                                      <Clock className="w-4 h-4 text-primary" />
                                    </div>

                                    {/* Content */}
                                    <div className="flex-1 pb-4 border-b border-border last:border-b-0">
                                      <div className="flex items-start justify-between">
                                        <div>
                                          <div className="flex items-center gap-2 mb-1">
                                            <span className="text-sm font-bold text-primary">{item.time}</span>
                                            <h4 className="font-semibold">{item.event}</h4>
                                          </div>
                                          {item.location && (
                                            <div className="flex items-center gap-1 text-sm text-muted-foreground mb-1">
                                              <MapPin className="w-3 h-3" />
                                              {item.location}
                                            </div>
                                          )}
                                          {item.notes && (
                                            <p className="text-sm text-muted-foreground">{item.notes}</p>
                                          )}
                                        </div>
                                      </div>
                                    </div>
                                  </motion.div>
                                ))}
                            </div>
                          </div>
                        ) : (
                          <div className="text-center py-10">
                            <ListChecks className="w-10 h-10 mx-auto mb-3 text-muted-foreground" />
                            <p className="text-muted-foreground">Brak punktow programu</p>
                            <Button size="sm" className="mt-3" onClick={() => setShowAgendaForm(true)}>
                              <Plus className="w-4 h-4 mr-2" />
                              Dodaj pierwszy punkt
                            </Button>
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  </TabsContent>

                  {/* Budget Tab */}
                  <TabsContent value="budget">
                    <div className="space-y-6">
                      {/* Budget Overview */}
                      <Card className="bg-card border-border">
                        <CardHeader>
                          <CardTitle>Podsumowanie budzetu</CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-4">
                          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                            <div className="p-4 bg-secondary/50 rounded-sm text-center">
                              <p className="text-sm text-muted-foreground mb-1">Budzet calkowity</p>
                              <p className="text-2xl font-bold">{formatCurrency(budgetTotal)}</p>
                            </div>
                            <div className="p-4 bg-secondary/50 rounded-sm text-center">
                              <p className="text-sm text-muted-foreground mb-1">Zaplanowane wydatki</p>
                              <p className={`text-2xl font-bold ${budgetSpent > budgetTotal ? 'text-red-500' : ''}`}>
                                {formatCurrency(budgetSpent)}
                              </p>
                            </div>
                            <div className="p-4 bg-secondary/50 rounded-sm text-center">
                              <p className="text-sm text-muted-foreground mb-1">Zaplacono</p>
                              <p className="text-2xl font-bold text-green-600">{formatCurrency(budgetPaid)}</p>
                            </div>
                          </div>

                          {budgetTotal > 0 && (
                            <div className="space-y-2">
                              <div className="flex items-center justify-between text-sm">
                                <span className="text-muted-foreground">Wykorzystanie budzetu</span>
                                <span className={`font-semibold ${budgetSpent > budgetTotal ? 'text-red-500' : ''}`}>
                                  {Math.round(budgetProgress)}%
                                </span>
                              </div>
                              <Progress value={budgetProgress} className={budgetSpent > budgetTotal ? '[&>div]:bg-red-500' : ''} />
                              {budgetTotal > budgetSpent && (
                                <p className="text-xs text-muted-foreground text-right">
                                  Pozostalo: {formatCurrency(budgetTotal - budgetSpent)}
                                </p>
                              )}
                              {budgetSpent > budgetTotal && (
                                <p className="text-xs text-red-500 text-right">
                                  Przekroczenie budzetu o {formatCurrency(budgetSpent - budgetTotal)}
                                </p>
                              )}
                            </div>
                          )}
                        </CardContent>
                      </Card>

                      {/* Budget Items */}
                      <Card className="bg-card border-border">
                        <CardHeader className="flex flex-row items-center justify-between">
                          <CardTitle>Pozycje budzetowe</CardTitle>
                          <Button size="sm" onClick={() => setShowBudgetForm(!showBudgetForm)}>
                            {showBudgetForm ? (
                              <>
                                <X className="w-4 h-4 mr-2" />
                                Anuluj
                              </>
                            ) : (
                              <>
                                <Plus className="w-4 h-4 mr-2" />
                                Dodaj pozycje
                              </>
                            )}
                          </Button>
                        </CardHeader>
                        <CardContent>
                          {/* Add Budget Item Form */}
                          <AnimatePresence>
                            {showBudgetForm && (
                              <motion.div
                                initial={{ opacity: 0, height: 0 }}
                                animate={{ opacity: 1, height: 'auto' }}
                                exit={{ opacity: 0, height: 0 }}
                                className="overflow-hidden"
                              >
                                <div className="p-4 mb-6 bg-secondary/30 rounded-sm border border-border space-y-4">
                                  <h4 className="font-semibold">Nowa pozycja</h4>
                                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                                    <div className="space-y-1">
                                      <Label>Kategoria *</Label>
                                      <Select value={budgetForm.category} onValueChange={(v) => setBudgetForm({ ...budgetForm, category: v })}>
                                        <SelectTrigger><SelectValue placeholder="Wybierz kategorie" /></SelectTrigger>
                                        <SelectContent>
                                          {BUDGET_CATEGORIES.map(c => (
                                            <SelectItem key={c} value={c}>{c}</SelectItem>
                                          ))}
                                        </SelectContent>
                                      </Select>
                                    </div>
                                    <div className="space-y-1">
                                      <Label>Opis</Label>
                                      <Input
                                        value={budgetForm.description}
                                        onChange={(e) => setBudgetForm({ ...budgetForm, description: e.target.value })}
                                        placeholder="np. Fotograf - sesja plenerowa"
                                      />
                                    </div>
                                    <div className="space-y-1">
                                      <Label>Kwota (PLN) *</Label>
                                      <Input
                                        type="number"
                                        value={budgetForm.amount}
                                        onChange={(e) => setBudgetForm({ ...budgetForm, amount: e.target.value })}
                                        placeholder="0"
                                      />
                                    </div>
                                    <div className="space-y-1">
                                      <Label>Status</Label>
                                      <div className="flex items-center gap-2 h-9">
                                        <Switch
                                          checked={budgetForm.paid}
                                          onCheckedChange={(checked) => setBudgetForm({ ...budgetForm, paid: checked })}
                                        />
                                        <span className="text-sm">{budgetForm.paid ? 'Zaplacone' : 'Do zaplaty'}</span>
                                      </div>
                                    </div>
                                  </div>
                                  <div className="flex justify-end gap-2">
                                    <Button variant="outline" onClick={() => { setShowBudgetForm(false); setBudgetForm({ ...emptyBudgetItem }); }}>
                                      Anuluj
                                    </Button>
                                    <Button onClick={handleAddBudgetItem}>
                                      Dodaj pozycje
                                    </Button>
                                  </div>
                                </div>
                              </motion.div>
                            )}
                          </AnimatePresence>

                          {/* Budget Items List */}
                          {budgetItems.length > 0 ? (
                            <div className="overflow-x-auto">
                              <Table>
                                <TableHeader>
                                  <TableRow>
                                    <TableHead className="pl-4">Kategoria</TableHead>
                                    <TableHead>Opis</TableHead>
                                    <TableHead className="text-right">Kwota</TableHead>
                                    <TableHead className="text-center">Status</TableHead>
                                    <TableHead className="text-center w-16">Akcja</TableHead>
                                  </TableRow>
                                </TableHeader>
                                <TableBody>
                                  {budgetItems.map((item, idx) => (
                                    <motion.tr
                                      key={idx}
                                      initial={{ opacity: 0 }}
                                      animate={{ opacity: 1 }}
                                      transition={{ delay: idx * 0.02 }}
                                      className="border-b transition-colors hover:bg-muted/50"
                                    >
                                      <TableCell className="pl-4 py-3">
                                        <span className="font-medium">{item.category}</span>
                                      </TableCell>
                                      <TableCell>
                                        <span className="text-sm text-muted-foreground">{item.description || '-'}</span>
                                      </TableCell>
                                      <TableCell className="text-right">
                                        <span className="font-semibold">{formatCurrency(item.amount)}</span>
                                      </TableCell>
                                      <TableCell className="text-center">
                                        <button
                                          onClick={() => handleToggleBudgetPaid(idx)}
                                          className="inline-flex items-center"
                                        >
                                          {item.paid ? (
                                            <Badge className="bg-green-500/20 text-green-600 border-green-500/30 cursor-pointer">
                                              <Check className="w-3 h-3 mr-1" />
                                              Zaplacone
                                            </Badge>
                                          ) : (
                                            <Badge className="bg-orange-500/20 text-orange-600 border-orange-500/30 cursor-pointer">
                                              <Clock className="w-3 h-3 mr-1" />
                                              Do zaplaty
                                            </Badge>
                                          )}
                                        </button>
                                      </TableCell>
                                      <TableCell className="text-center">
                                        <Button
                                          variant="ghost"
                                          size="sm"
                                          className="text-destructive hover:text-destructive"
                                          onClick={() => handleRemoveBudgetItem(idx)}
                                        >
                                          <Trash2 className="w-4 h-4" />
                                        </Button>
                                      </TableCell>
                                    </motion.tr>
                                  ))}
                                </TableBody>
                              </Table>
                            </div>
                          ) : (
                            <div className="text-center py-10">
                              <Wallet className="w-10 h-10 mx-auto mb-3 text-muted-foreground" />
                              <p className="text-muted-foreground">Brak pozycji budzetowych</p>
                              <Button size="sm" className="mt-3" onClick={() => setShowBudgetForm(true)}>
                                <Plus className="w-4 h-4 mr-2" />
                                Dodaj pierwsza pozycje
                              </Button>
                            </div>
                          )}
                        </CardContent>
                      </Card>
                    </div>
                  </TabsContent>
                </Tabs>
              </>
            )}
          </motion.div>
        )}

        {/* Create Wedding Modal */}
        <AnimatePresence>
          {showCreateModal && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4"
              onClick={() => setShowCreateModal(false)}
            >
              <motion.div
                initial={{ scale: 0.95 }}
                animate={{ scale: 1 }}
                exit={{ scale: 0.95 }}
                className="bg-card rounded-sm max-w-md w-full p-6"
                onClick={(e) => e.stopPropagation()}
              >
                <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                  <Heart className="w-5 h-5 text-pink-500" />
                  Nowy plan wesela
                </h3>
                <div className="space-y-4">
                  <div className="space-y-1">
                    <Label>Nazwa pary *</Label>
                    <Input
                      value={createForm.couple_name}
                      onChange={(e) => setCreateForm({ ...createForm, couple_name: e.target.value })}
                      placeholder="Anna i Tomek"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label>Data slubu *</Label>
                    <Input
                      type="date"
                      value={createForm.wedding_date}
                      onChange={(e) => setCreateForm({ ...createForm, wedding_date: e.target.value })}
                    />
                  </div>
                  <div className="space-y-1">
                    <Label>Budzet calkowity (PLN)</Label>
                    <Input
                      type="number"
                      value={createForm.budget_total}
                      onChange={(e) => setCreateForm({ ...createForm, budget_total: e.target.value })}
                      placeholder="50000"
                    />
                  </div>
                  <div className="flex items-center gap-3">
                    <Switch
                      checked={createForm.public}
                      onCheckedChange={(checked) => setCreateForm({ ...createForm, public: checked })}
                    />
                    <div>
                      <p className="text-sm font-medium">Publiczny link</p>
                      <p className="text-xs text-muted-foreground">
                        Udostepnij gosciom plan wesela
                      </p>
                    </div>
                  </div>
                  {createForm.public && (
                    <div className="space-y-1">
                      <Label className="flex items-center gap-1">
                        <Lock className="w-3 h-3" />
                        Haslo dostepu (opcjonalne)
                      </Label>
                      <Input
                        type="password"
                        value={createForm.password}
                        onChange={(e) => setCreateForm({ ...createForm, password: e.target.value })}
                        placeholder="Pozostaw puste dla otwartego dostepu"
                      />
                    </div>
                  )}
                </div>
                <div className="flex gap-2 mt-6">
                  <Button variant="outline" onClick={() => setShowCreateModal(false)} className="flex-1">
                    Anuluj
                  </Button>
                  <Button onClick={handleCreateWedding} disabled={creating} className="flex-1">
                    {creating && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                    Utworz plan
                  </Button>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Delete Confirmation Modal */}
        <AnimatePresence>
          {deleteConfirm && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4"
              onClick={() => setDeleteConfirm(null)}
            >
              <motion.div
                initial={{ scale: 0.95 }}
                animate={{ scale: 1 }}
                exit={{ scale: 0.95 }}
                className="bg-card rounded-sm max-w-sm w-full p-6"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="text-center">
                  <div className="w-12 h-12 rounded-full bg-red-500/10 flex items-center justify-center mx-auto mb-4">
                    <Trash2 className="w-6 h-6 text-red-500" />
                  </div>
                  <h3 className="text-lg font-semibold mb-2">Usunac plan wesela?</h3>
                  <p className="text-sm text-muted-foreground mb-6">
                    Ta operacja jest nieodwracalna. Wszystkie dane gosci, agenda i budzet zostana usuniete.
                  </p>
                  <div className="flex gap-2">
                    <Button variant="outline" onClick={() => setDeleteConfirm(null)} className="flex-1">
                      Anuluj
                    </Button>
                    <Button
                      variant="destructive"
                      onClick={() => handleDeleteWedding(deleteConfirm)}
                      className="flex-1"
                    >
                      <Trash2 className="w-4 h-4 mr-2" />
                      Usun
                    </Button>
                  </div>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </main>
    </div>
  );
}
